import type { DispatchAllocations, DispatchPreview } from '../dispatch.api';
import type { useDispatchPermissions } from '../hooks/use-dispatch-permissions';
import { dispatchBlockers } from '../policies/dispatch-blockers.policy';
import { DispatchAllocationsForm } from './DispatchAllocationsForm';
import { DispatchCarryForward } from './DispatchCarryForward';
import { DispatchedPanels } from './DispatchedPanels';
import { DispatchEligibility } from './DispatchEligibility';
import { DispatchPlanCard } from './DispatchPlanCard';
import { DispatchReadiness } from './DispatchReadiness';

interface DispatchTripOverviewProps {
  preview: DispatchPreview;
  permissions: ReturnType<typeof useDispatchPermissions>;
  isSubmitting: boolean;
  error: string | null;
  onDispatch: (allocations: DispatchAllocations) => Promise<void>;
  onChanged: () => void;
}

/** The last look before a trip leaves, and once it has gone, what was given and the actions on the trip itself. */
export function DispatchTripOverview({ preview, permissions, isSubmitting, error, onDispatch, onChanged }: DispatchTripOverviewProps) {
  const { canDispatch, canApproveOverrides } = permissions;
  const blockers = canDispatch ? dispatchBlockers(preview).map((blocker) => blocker.message) : ['You do not have permission to dispatch trips.'];
  const driver = preview.crew.driver;
  return (
    <>
      <DispatchPlanCard preview={preview} />
      {!preview.dispatch && driver && <DispatchCarryForward driverName={driver.label} owes={preview.driverOwes} />}
      <DispatchReadiness items={preview.readiness.items} />
      <DispatchEligibility preview={preview} canApprove={canApproveOverrides} onOverridden={onChanged} />
      {preview.dispatch ? (
        <DispatchedPanels preview={{ ...preview, dispatch: preview.dispatch }} canDispatch={canDispatch} onChanged={onChanged} />
      ) : (
        <DispatchAllocationsForm blockers={blockers} isSubmitting={isSubmitting} error={error} onDispatch={onDispatch} />
      )}
    </>
  );
}
